import { useCallback } from 'react';
import { track } from '../../redux/actions/analytics';
import {
  createErrorMessageForGeneralCase,
  displaySentNewAuthCodeMessage,
  failAuthRequest,
} from '../../redux/actions/auth';
import { setAuthConfig } from '../../redux/actions/authConfig';
import { setIsRegistering } from '../../redux/actions/authProgress';
import { setAuthMessage } from '../../redux/actions/common';
import { getAuthCodeRequestOptions } from '../../redux/selectors/auth';
import { getEmailAddress, getNormalizedIdent } from '../../redux/selectors/authProgress';
import { AuthMethod, AuthType } from '../../types/analytics';
import { AuthAPIResponse } from '../../types/auth';
import {
  BAD_PHONE_NUMBER_MESSAGE,
  DEFAULT_ERROR,
  EMAIL_AUTHENTICATE_NEED_VERIFY_ERROR,
  FAIL_TYPE_EMAIL_NOT_FOUND,
  FAIL_TYPE_GENERIC,
  FAIL_TYPE_MOBILE_NUMBER_NOT_FOUND,
  FAIL_TYPE_MOBILE_NUMBER_STOPPED,
  FAIL_TYPE_NEED_VERIFY,
  PHONE_AUTHENTICATE_NEED_VERIFY_ERROR,
} from '../../utils/auth-errors';
import { authRequest } from '../../utils/auth-request';
import { AUTHORIZE_NESTED } from '../../utils/router';
import useInAppNavigate from '../router/use-in-app-navigate';
import useAppStore from '../use-app-store';
import useAuthRequestCallbacks from './use-auth-request-callbacks';
import { SIGNIN_SUCCESS } from '../../constants/analytics';

export const KNOWN_LOGIN_INIT_ERRORS = new Set([
  FAIL_TYPE_EMAIL_NOT_FOUND,
  FAIL_TYPE_GENERIC,
  FAIL_TYPE_MOBILE_NUMBER_NOT_FOUND,
  FAIL_TYPE_MOBILE_NUMBER_STOPPED,
]);

/** KU
 *
 * Returns a handler, {@link handleGetRegisteredUserAuthCode}, which starts the
 * sign-in process by requesting an auth code for the user. If the request is
 * successful (and `shouldRedirect=true`), the user is redirected to
 * `/authorize`.
 */
export default function useGetRegisteredUserAuthCode() {
  const { getState, dispatch } = useAppStore();
  const authRequestCallbacks = useAuthRequestCallbacks();
  const navigate = useInAppNavigate();

  const handleGetRegisteredUserAuthCode = useCallback((shouldRedirect = true) => {
    const state = getState();
    const {
      config,
      authConfig: { loginInitURL: requestAuthTokenURL },
    } = state;

    const isEmailAuthentication = !!getEmailAddress(state);
    const ident = getNormalizedIdent(state);

    if (!isEmailAuthentication && !ident) {
      dispatch(failAuthRequest({ message: BAD_PHONE_NUMBER_MESSAGE }));
      return;
    }
    // never got urls from status check
    if (!requestAuthTokenURL) {
      dispatch(failAuthRequest({ message: DEFAULT_ERROR }));
      return;
    }

    const onAuthSuccess = ({
      sent_confirm_link: sentConfirmLink,
      polling_interval_seconds: pollingIntervalSeconds,
      sent_token: sentToken,
    }: AuthAPIResponse) => {
      const authType = isEmailAuthentication ? AuthType.EMAIL : AuthType.PHONE;
      dispatch(
        track(SIGNIN_SUCCESS, {
          authType,
          method: AuthMethod[authType],
          sentConfirmLink,
          sentToken,
        }),
      );
      dispatch(setAuthConfig({ sentConfirmLink, pollingIntervalSeconds }));
      if (shouldRedirect) {
        navigate(AUTHORIZE_NESTED);
      } else {
        dispatch(displaySentNewAuthCodeMessage());
      }
    };
    const onAuthFailure = ({
      error_code: errorCode,
      description,
      sent_confirm_link: sentConfirmLink,
    }: AuthAPIResponse) => {
      if (errorCode === FAIL_TYPE_NEED_VERIFY) {
        let authMessage = null;
        if (isEmailAuthentication) {
          authMessage = EMAIL_AUTHENTICATE_NEED_VERIFY_ERROR;
        } else {
          authMessage = PHONE_AUTHENTICATE_NEED_VERIFY_ERROR;
        }
        dispatch(setAuthConfig({ sentConfirmLink }));
        dispatch(setAuthMessage(authMessage));
        if (shouldRedirect) {
          navigate(AUTHORIZE_NESTED);
        }
      } else {
        const message = KNOWN_LOGIN_INIT_ERRORS.has(errorCode)
          ? description
          : createErrorMessageForGeneralCase(description, errorCode);
        dispatch(failAuthRequest({ message, errorCode }));
      }
    };

    dispatch(setIsRegistering(false));

    const options = getAuthCodeRequestOptions(state);
    const requestBody = JSON.stringify({
      ...options,
      send_confirm_link: config.sendConfirmLink && options.include_login_link,
    });

    authRequest({
      url: requestAuthTokenURL,
      requestBody,
      ...authRequestCallbacks,
      onAuthSuccess,
      onAuthFailure,
    });
  }, []);

  return { handleGetRegisteredUserAuthCode };
}
